import axios, { AxiosRequestHeaders } from "axios";
import { GraphQLError } from "graphql";
import { getSession } from "next-auth/react";

const baseURL = (process.env.PROVIDER_HASURA_GRAPHQL_ENDPOINT || " ")?.replace(
  "/graphql",
  "",
);
export const createPremiumApiClient = () => {
  const defaultOptions = {
    baseURL,
  };

  const instance = axios.create(defaultOptions);

  instance.interceptors.request.use(async (req) => {
    const session = await getSession();
    if (session?.accessToken) {
      req = {
        ...req,
        headers: {
          Authorization: `Bearer ${session?.accessToken || ""}`,
          ...req?.headers,
        },
      };
    }
    return req;
  });

  instance.interceptors.response.use(
    (res) => {
      return res;
    },
    (error) => {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error("PremiumApiClient Error", error.response.data);
        console.error("PremiumApiClient Error Status", error.response.status);
        console.error("PremiumApiClient Error Headers", error.response.headers);
      } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        console.error("PremiumApiClient Error Request", error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error("PremiumApiClient Error", error.message);
      }
      console.error(error.config);
    },
  );

  return instance;
};

const premiumApiClient = createPremiumApiClient();

/**
 * Used as the fetcher config for graphql-code-generator's react-query config with [isReactHook: false](https://www.graphql-code-generator.com/plugins/typescript/typescript-react-query#usage-example-isreacthook-false)
 */
export const premiumApiGraphQLFetcher = <TData, TVariables>(
  query: string,
  variables?: TVariables,
  options?: AxiosRequestHeaders,
): (() => Promise<TData>) => {
  return async () => {
    const { data } = await premiumApiClient.post<{
      data: TData & { errors?: GraphQLError[] };
      errors?: GraphQLError[];
    }>(
      "/graphql",
      {
        query,
        variables,
      },
      {
        headers: {
          "Content-Type": "application/json",
          ...options,
        },
      },
    );
    if (data?.errors) {
      throw new Error(data.errors?.[0]?.message);
    }
    return data.data;
  };
};

/**
 * Used for all content fetched from the cms. I.E subcategory content
 */
const contentClient = axios.create({
  baseURL,
  headers: {
    "Access-Control-Allow-Origin": "*",
    "x-hasura-admin-secret": process.env.PROVIDER_HASURA_ADMIN_SECRET as string,
  },
});

export const contentGraphQLFetcher = <TData, TVariables>(
  query: string,
  variables?: TVariables,
  options?: AxiosRequestHeaders,
): (() => Promise<TData>) => {
  return async () => {
    const { data } = await contentClient.post<{
      data: TData;
      errors?: GraphQLError[];
    }>("/graphql", {
      headers: {
        "Content-Type": "application/json",
        ...options,
      },
      query,
      variables,
    });
    if (data.errors) {
      throw new Error(data.errors[0].message);
    }
    return data.data;
  };
};
